import type { CustomFile } from '../types';

import type { TypeUploadConfig, TypeUploadOnceResult, TypeResolvedFeatures } from './types';
import type { TypeUploadContext } from './createUploadContext';
import type { TypeUploadProgress } from './createUploadProgress';
import type { TypeRequestRegistry } from './createRequestRegistry';
import type { TypeUploadFeature, TypeUploadPart } from './core/types';

import sendRequest from './core/sendRequest';
import prepareUploadRequest from './prepareUploadRequest';
import { EmptyUploadError } from './errors';

// ----------------------------------------------------------------------------

export type TypeUploadOnceDeps = {
    config: TypeUploadConfig;
    features: TypeResolvedFeatures;
    plugins: TypeUploadFeature[];
    context: TypeUploadContext;
    progress: TypeUploadProgress;
    registry: TypeRequestRegistry;
    // Set when the chunk feature split the file; absent for a whole-file attempt.
    part?: TypeUploadPart;
};

// ----------------------------------------------------------------------------

/*
 * The headers a chunked request carries when the caller has not described its own format. This is
 * a common convention, not a standard: `fnHeaders` is how a server that expects an upload id, a
 * different range syntax or anything else gets what it needs.
 */
function defaultChunkHeaders(part: TypeUploadPart, file: CustomFile): Record<string, string> {
    return {
        'Content-Range': `bytes ${part.start}-${part.end - 1}/${file.size}`,
        'X-Chunk-Index': String(part.index),
        'X-Chunk-Total': String(part.total),
    };
}

// ----------------------------------------------------------------------------

/*
 * One request for one file, or for one slice of it: builds the body, hands it to the transport and
 * reports bytes to the progress accounting. Everything optional around the request — stalls,
 * retries, timeouts, chunking — comes from the features, so this stays the body-building step.
 */
async function uploadOnceFile(file: CustomFile, deps: TypeUploadOnceDeps): Promise<TypeUploadOnceResult> {
    const { config, features, plugins, context, progress, registry, part } = deps;

    let body;
    let expectedBytes;

    try {
        ({ body, expectedBytes } = await prepareUploadRequest(file, config, part));
    } catch (err: any) {
        if (context.abortedFlag.aborted) {
            return { ok: false, status: 0, reason: 'abort' };
        }

        // Deterministic prepare failures (empty file/body) are permanent; others may be transient.
        return {
            ok: false,
            status: 0,
            reason: 'error',
            retryable: !(err instanceof EmptyUploadError),
            message: err?.message ?? 'Failed to prepare upload',
        };
    }

    /*
     * Progress is always accounted against the whole file, never the slice: a chunked upload has to
     * read as one file advancing, not as N files each finishing at 100%.
     */
    const totalBytes = part ? file.size : expectedBytes;
    const offset = part?.start ?? 0;

    progress.setExpected(file, totalBytes);
    progress.emit();

    const headers = part ? (features.chunk.fnHeaders ?? defaultChunkHeaders)(part, file) : undefined;
    const isLastPart = !part || part.index === part.total - 1;

    return sendRequest({
        config,
        features,
        plugins,
        context,
        registry,
        body,
        headers,
        label: part ? `${file.name} (${part.index + 1}/${part.total})` : file.name,
        fnUploadProgress: (evt): void => {
            progress.setLoaded(file, offset + (evt.loaded ?? 0));
            progress.emit();
        },
        fnSuccess: (): void => {
            // Only the last slice completes the file; the others just move it forward.
            if (isLastPart) {
                progress.markUploaded(file, totalBytes);
                return;
            }

            progress.setLoaded(file, part.end);
        },
    });
}

// ----------------------------------------------------------------------------

export default uploadOnceFile;
